/**
 * Galaxy Work Items View — list + detail.
 *
 * Filter state lives in ./work_state (reset-page-on-change + debounce).
 * Filter bar / pagination HTML lives in ./work_filters.
 * Row + detail HTML lives in ./work_render.
 * This module: coordinates fetch → render, owns routes.
 *
 * Search and filter don't compose server-side: when `search` is
 * non-empty we hit /api/v2/work/search (no filters), otherwise
 * /api/v2/work/items with the full filter set. Stats always come from
 * /api/v2/work/stats and drive filter-button counts.
 */

import { route } from '../../router.js'
import { esc } from '../../shared/rendering.js'
import { action } from '../components/action-delegation.js'
import {
  fetchWorkItems,
  searchWorkItems,
  fetchWorkStats,
  fetchWorkAssignees,
  fetchWorkItemDetail,
  type WorkListMeta,
} from '../../api/work.js'
import type { WorkItem } from '../../types/api.js'
import { renderWorkList, renderWorkDetail } from './work_render.js'
import { renderWorkFilters, renderTagCloudContents } from './work_filters.js'
import { renderWorkSubtitle, renderWorkPagination } from './work_list_chrome.js'
import {
  initWorkState,
  getWorkFilters,
  getWorkStats,
  setWorkStatsSnapshot,
  getWorkAssignees,
  setWorkAssigneesSnapshot,
  hasActiveFilters,
} from './work_state.js'

export {
  setWorkTypeFilter,
  setWorkStatusFilter,
  setWorkPriorityFilter,
  setWorkAssignee,
  toggleWorkTag,
  setWorkSort,
  setWorkSearchInput,
  clearWorkFilters,
  setWorkPage,
} from './work_state.js'

// ─── Routes ─────────────────────────────────────────────────────────────────

route('/work', () => {
  void renderWorkListView()
})
route('/work/:item_id', (params) => {
  if (params.item_id) {
    void renderWorkDetailView(params.item_id)
  }
})

initWorkState(() => {
  void renderWorkListView()
})

// ─── Module State ────────────────────────────────────────────────────────────

let _workGen = 0
const WORK_PAGE_SIZE = 50

// ─── List View ──────────────────────────────────────────────────────────────

async function renderWorkListView(): Promise<void> {
  const gen = ++_workGen
  const app = document.getElementById('app')
  if (!app) {
    return
  }
  document.title = 'Work Items — Grizzly'

  const f = getWorkFilters()
  app.innerHTML = renderShell()

  try {
    const result = await fetchList()
    if (_workGen !== gen) {
      return
    }
    void refreshStats(gen)
    void refreshAssignees(gen)
    swapListContent(result.data, result.meta)
  } catch (err) {
    if (_workGen !== gen) {
      return
    }
    const content = document.getElementById('work-content')
    if (content) {
      content.innerHTML = `<div class="empty" style="padding:40px;text-align:center;color:var(--t3)">Failed to load work items. ${esc(String(err))}</div>`
    }
  }
  void f
}

function renderShell(): string {
  const f = getWorkFilters()
  const stats = getWorkStats()
  const subtitle = renderWorkSubtitle(0, undefined, stats, hasActiveFilters())
  return `<header class="page-header">
    <div>
      <h1 class="page-title">Work Items</h1>
      <p class="page-subtitle">${subtitle}</p>
    </div>
    <button class="cmd-k" ${action('openCommandPalette')}>Search <kbd>⌘K</kbd></button>
  </header>
  ${renderWorkFilters(f, stats, getWorkAssignees())}
  <div id="work-content"><div class="loading"><div class="spinner"></div></div></div>`
}

function swapListContent(items: readonly WorkItem[], meta: WorkListMeta | undefined): void {
  const f = getWorkFilters()
  const content = document.getElementById('work-content')
  if (!content) {
    return
  }
  content.innerHTML = renderWorkList(items) + renderWorkPagination(meta, f.page)

  // Tag cloud — rebuild from loaded rows.
  const cloud = document.getElementById('wi-tag-cloud')
  if (cloud) {
    const allTags = items.flatMap((i) => i.tags || [])
    cloud.outerHTML = `<div id="wi-tag-cloud">${renderTagCloudContents(allTags, f.tags)}</div>`
  }

  // Subtitle — refresh with real counts.
  const subtitle = document.querySelector('.page-subtitle')
  if (subtitle) {
    subtitle.innerHTML = renderWorkSubtitle(
      items.length,
      meta?.total,
      getWorkStats(),
      hasActiveFilters(),
    )
  }
}

function fetchList(): Promise<{ data: WorkItem[]; meta?: WorkListMeta }> {
  const f = getWorkFilters()
  if (f.search.trim()) {
    return searchWorkItems(f.search.trim(), {
      ...(f.type ? { type: f.type } : {}),
      page: f.page,
      page_size: WORK_PAGE_SIZE,
    })
  }
  return fetchWorkItems({
    ...(f.type ? { type: f.type } : {}),
    ...(f.status ? { status: f.status } : {}),
    ...(f.priority ? { priority: f.priority } : {}),
    ...(f.assignee ? { assignee: f.assignee } : {}),
    ...(f.tags.length ? { tag: f.tags } : {}),
    order_by: f.orderBy,
    order: f.order,
    page: f.page,
    page_size: WORK_PAGE_SIZE,
  })
}

/**
 * Load the distinct assignee list for the People filter and re-render the
 * filter bar. Best-effort and non-fatal, like stats: a failure just leaves
 * the People group with only "Anyone" and "Me".
 */
async function refreshAssignees(gen: number): Promise<void> {
  try {
    const { assignees } = await fetchWorkAssignees()
    if (_workGen !== gen) {
      return
    }
    setWorkAssigneesSnapshot(assignees)
    const existingFilters = document.querySelector('.wi-filters')
    if (existingFilters) {
      existingFilters.outerHTML = renderWorkFilters(getWorkFilters(), getWorkStats(), assignees)
    }
  } catch {
    // Non-fatal — the People filter still offers Anyone / Me.
  }
}

async function refreshStats(gen: number): Promise<void> {
  try {
    const s = await fetchWorkStats()
    if (_workGen !== gen) {
      return
    }
    setWorkStatsSnapshot(s)
    // Re-render just the filter bar with updated counts.
    const existingFilters = document.querySelector('.wi-filters')
    if (existingFilters) {
      existingFilters.outerHTML = renderWorkFilters(getWorkFilters(), s, getWorkAssignees())
    }
  } catch {
    // Stats failure is non-fatal.
  }
}

// ─── Detail View ────────────────────────────────────────────────────────────

async function renderWorkDetailView(itemId: string): Promise<void> {
  const gen = ++_workGen
  const app = document.getElementById('app')
  if (!app) {
    return
  }
  document.title = `${itemId} — Grizzly`
  app.innerHTML = '<div class="loading"><div class="spinner"></div></div>'
  try {
    const item = await fetchWorkItemDetail(itemId)
    if (_workGen !== gen) {
      return
    }
    const currentApp = document.getElementById('app')
    if (!currentApp) {
      return
    }
    currentApp.innerHTML = renderWorkDetail(item)
    currentApp.focus({ preventScroll: true })
  } catch (err) {
    if (_workGen !== gen) {
      return
    }
    const currentApp = document.getElementById('app')
    if (currentApp) {
      currentApp.innerHTML = `<div class="empty" style="padding:40px;text-align:center;color:var(--t3)">Failed to load ${esc(itemId)}. ${esc(String(err))}</div>`
    }
  }
}
